// Analytics utility for tracking events to Google Tag Manager and Amplitude

import { trackAmplitudeEvent } from './amplitudeLoader';

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  customProperties?: Record<string, unknown>;
}

// Google Tag Manager tracking
export const trackGTMEvent = (event: AnalyticsEvent) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'custom_event',
      event_action: event.action,
      event_category: event.category,
      event_label: event.label,
      event_value: event.value,
      ...event.customProperties,
    });
  }
};

// Amplitude tracking - now uses optimized loader
export const trackAmplitudeEventOptimized = (event: AnalyticsEvent) => {
  trackAmplitudeEvent(event.action, {
    category: event.category,
    label: event.label,
    value: event.value,
    ...event.customProperties,
  });
};

// Track to both Google Tag Manager and Amplitude
export const trackEvent = (event: AnalyticsEvent) => {
  trackGTMEvent(event);
  trackAmplitudeEventOptimized(event);
};

// Page view tracking
export const trackPageView = (url: string, title?: string) => {
  if (typeof window !== 'undefined') {
    // Google Tag Manager page view
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: url,
        page_title: title,
      });
    }
    
    // Amplitude page view - using optimized loader
    trackAmplitudeEvent('Page Viewed', {
      page_path: url,
      page_title: title,
    });
  }
};

// Button click tracking
export const trackButtonClick = (
  buttonName: string,
  category: string = 'Button Click',
  customProperties?: Record<string, unknown>
) => {
  trackEvent({
    action: 'click',
    category,
    label: buttonName,
    customProperties: {
      button_name: buttonName,
      ...customProperties,
    },
  });
};

// Payment completion tracking
export const trackPaymentCompletion = (
  amount: number,
  currency: string = 'INR',
  paymentMethod?: string,
  bookingId?: string,
  customProperties?: Record<string, unknown>
) => {
  trackEvent({
    action: 'purchase',
    category: 'Ecommerce',
    value: amount,
    customProperties: {
      currency,
      payment_method: paymentMethod,
      booking_id: bookingId,
      transaction_id: bookingId,
      ...customProperties,
    },
  });
};

// Search tracking
export const trackSearch = (
  searchTerm: string,
  category: string = 'Search',
  resultsCount?: number,
  customProperties?: Record<string, unknown>
) => {
  trackEvent({
    action: 'search',
    category,
    label: searchTerm,
    value: resultsCount,
    customProperties: {
      search_term: searchTerm,
      results_count: resultsCount,
      ...customProperties,
    },
  });
};

// Booking tracking
export const trackBooking = (
  action: 'start' | 'complete' | 'cancel',
  bookingId?: string,
  amount?: number,
  customProperties?: Record<string, unknown>
) => {
  trackEvent({
    action: `booking_${action}`,
    category: 'Booking',
    value: amount,
    customProperties: {
      booking_id: bookingId,
      ...customProperties,
    },
  });
};
